import { REGION_OPTIONS } from "src/utils/regionUtils";
import {
  ChangeFilter,
  ChangeType,
  CoverageFilter,
  FailureKind,
  FileType,
  OverviewTotals,
  PolicyDocument,
  ReasonCode,
  RegionCode,
  RegionReport,
} from "./types";

/** File type columns, in the order they are shown in tables, charts and legends. */
/** The types the harness reports on. "other" absorbs anything else it counted. */
export const KNOWN_FILE_TYPES: FileType[] = ["pdf", "doc", "excel", "ppt"];

export const FILE_TYPES: FileType[] = [...KNOWN_FILE_TYPES, "other"];

export const FILE_TYPE_LABELS: Record<FileType, string> = {
  pdf: "PDF",
  doc: "Doc",
  excel: "Excel",
  ppt: "PPT",
  other: "Other",
};

/** Plotly series colors, kept in sync with the chart legends. */
export const FILE_TYPE_COLORS: Record<FileType, string> = {
  pdf: "#1768c9",
  doc: "#1aa06d",
  excel: "#e8871e",
  ppt: "#9b51e0",
  other: "#8a94a6",
};

export const asFileType = (fileType?: string | null): FileType =>
  KNOWN_FILE_TYPES.indexOf(fileType as FileType) === -1
    ? "other"
    : (fileType as FileType);

/** Pass rate at or above this is healthy, below PASS_RATE_AT_RISK is failing. */
export const PASS_RATE_HEALTHY = 90;
export const PASS_RATE_AT_RISK = 70;

/** Region codes double as their own short display label once upper-cased. */
export const regionShortLabel = (region: RegionCode) => region.toUpperCase();

export const regionFullLabel = (region: RegionCode) =>
  REGION_OPTIONS.find((option) => option.value === region)?.label ??
  regionShortLabel(region);

export const REGION_CODES = REGION_OPTIONS.map(
  ({ value }) => value as RegionCode,
);

export const isRegionCode = (value: string): value is RegionCode =>
  REGION_CODES.includes(value as RegionCode);

/** Region order used by the report, matching the regional performance table. */
export const REPORT_REGION_ORDER: RegionCode[] = [
  "na",
  "eu",
  "au",
  "jp",
  "latam",
  "mena",
  "in",
  "sg",
];

/**
 * Policy tree link. The service derives this, so prefer its value and only fall
 * back to building one when the row could not address a policy page.
 */
export const policyLink = (document: PolicyDocument) => {
  if (document.policyUrl) return document.policyUrl;
  if (!document.policyId || !document.nodeInstanceType) return null;

  return `https://policy.a2z.com/docs/${document.policyId}/${document.nodeInstanceType}`;
};

export const regionTotalFiles = (report: RegionReport) =>
  FILE_TYPES.reduce(
    (total, fileType) => total + report.fileCounts[fileType],
    0,
  );

/** True when the region has recorded a run, so pass/fail mean something. */
export const hasRun = (report: RegionReport) => report.passed != null;

/**
 * Pass rate as a whole percentage, null when the region has never run. Measured
 * against the cases the run actually recorded, not the cases that exist, so a
 * part-finished run is not reported as failing.
 *
 * Floored, not rounded: 1099 of 1101 rounds up to 100%, which reads as a clean
 * run next to a Failed card showing 2. Only a genuine 100% shows 100%.
 */
export const regionPassRate = (report: RegionReport): number | null => {
  if (!hasRun(report)) return null;
  // ?? not ||: a run that genuinely recorded 0 must stay 0 and fall into the
  // guard below, not silently measure against every case that exists.
  const recorded = report.testCasesRecorded ?? report.testCaseCount;
  // A run against zero cases has no rate. Returning 0 would draw a red 0%
  // meter, reading as total failure rather than nothing to measure.
  if (!recorded) return null;

  return Math.floor((report.passed / recorded) * 100);
};

/** Per file type, summed the same way as overviewTotals. See the note there. */
export const fileTypeTotals = (regions: RegionReport[]) =>
  FILE_TYPES.map((fileType) => ({
    fileType,
    count: regions.reduce(
      (total, region) => total + region.fileCounts[fileType],
      0,
    ),
  }));

/**
 * Overview totals, counted in region-indexed files rather than distinct files.
 *
 * A global file is promoted into all eight regional tables, so it is counted
 * once per region here. That is deliberate, not a double count: each regional
 * copy carries its own test case, is tested by that region's own run, and can
 * be covered in one region while uncovered in another, so each copy is a
 * separate thing to cover.
 *
 * It also keeps the three cards reconcilable. The service counts each region's
 * documents, test cases and uncovered files from one scan, so per region
 * `searchable = test cases + uncovered`. Summing all three the same way
 * preserves that; de-duplicating globals from the document total alone would
 * leave Total Documents smaller than Test Cases + Uncovered.
 *
 * Distinct-file counts are not derivable here in any case: the overview
 * response carries counts only, no attachment ids.
 */
export const overviewTotals = (regions: RegionReport[]): OverviewTotals => {
  // Averaged over regions that have run, so a never-run region does not drag
  // the figure down as if it had scored zero.
  const rated = regions
    .map(regionPassRate)
    .filter((rate): rate is number => rate !== null);

  return {
    totalDocuments: regions.reduce(
      (total, region) => total + regionTotalFiles(region),
      0,
    ),
    totalTestCases: regions.reduce(
      (total, region) => total + region.testCaseCount,
      0,
    ),
    // Floored for the same reason as regionPassRate: regions at 99 and 100
    // average to 99.5, and rounding would report every region as clean.
    averagePassRate: rated.length
      ? Math.floor(rated.reduce((sum, rate) => sum + rate, 0) / rated.length)
      : 0,
    failedTestCases: regions.reduce(
      (total, region) => total + (region.failed ?? 0),
      0,
    ),
    uncoveredDocuments: regions.reduce(
      (total, region) => total + region.uncoveredDocumentCount,
      0,
    ),
  };
};

/**
 * Indexed for search but with no test case, so nothing checks it can still be
 * found. The documents endpoint only returns indexed files, so the absence of a
 * test case is the whole test.
 */
export const isUncovered = (document: PolicyDocument) => !document.testCase;

/** A document still in the policy tree: anything the sync did not delete. */
export const isCurrentDocument = (document: PolicyDocument) =>
  GONE_FROM_INDEX.indexOf(document.changeType) === -1;

export const uncoveredDocumentCount = (documents: PolicyDocument[]) =>
  documents.filter(
    (document) => isCurrentDocument(document) && isUncovered(document),
  ).length;

/**
 * Display labels for what the sync did. The publisher owns the codes; these
 * keep the publisher's own wording so nothing is reinterpreted.
 */
export const CHANGE_TYPE_LABELS: Record<ChangeType, string> = {
  EXISTING: "No Change",
  NEW: "Newly Added",
  VERSION_UPDATE: "Version Upgrade",
  METADATA_UPDATE: "Metadata Update",
  NOW_INCLUDED: "Now Included",
  NOW_EXCLUDED: "Now Excluded",
  RESTORED: "Restored",
  REMOVED: "Removed",
};

/**
 * Filter options, in the order they are offered. Only these four can occur in a
 * list of searchable files; the other four describe a file leaving or rejoining
 * the index. CHANGE_TYPE_LABELS still covers all eight, so an unexpected value
 * renders with its label rather than breaking.
 */
export const CHANGE_TYPES: ChangeType[] = [
  "VERSION_UPDATE",
  "NEW",
  "METADATA_UPDATE",
  "EXISTING",
];

/**
 * A file the index no longer serves. Its test case can only fail, so these are
 * excluded from the uncovered count.
 */
export const GONE_FROM_INDEX: ChangeType[] = ["REMOVED", "NOW_EXCLUDED"];

export const matchesChangeFilter = (
  document: PolicyDocument,
  filter: ChangeFilter,
) => filter === "all" || document.changeType === filter;

export const matchesCoverageFilter = (
  document: PolicyDocument,
  filter: CoverageFilter,
) => {
  if (filter === "all") return true;

  return filter === "uncovered"
    ? isUncovered(document)
    : !isUncovered(document);
};

/**
 * Infrastructure failures cannot be fixed by rewording the question, so the
 * Failed tab disables Edit and Remove on them.
 */
export const FAILURE_KIND_BY_REASON: Record<ReasonCode, FailureKind> = {
  PASS: "content",
  WRONG_VERSION: "content",
  NOT_CITED: "content",
  NO_REFERENCES: "content",
  EMPTY_RESPONSE: "content",
  AUTH_FAILED: "infrastructure",
  REQUEST_FAILED: "infrastructure",
};

export const REASON_CODE_LABELS: Record<string, string> = {
  PASS: "Pass",
  WRONG_VERSION: "Wrong version",
  NOT_CITED: "Not cited",
  NO_REFERENCES: "No references",
  EMPTY_RESPONSE: "Empty response",
  AUTH_FAILED: "Auth failed",
  REQUEST_FAILED: "Request failed",
};

/** The harness can record a code the UI has no label for; show it as-is. */
export const reasonCodeLabel = (reasonCode: string) =>
  REASON_CODE_LABELS[reasonCode] ??
  reasonCode.charAt(0) + reasonCode.slice(1).toLowerCase().replace(/_/g, " ");

/** Unknown codes are treated as content failures, so the actions stay available. */
export const isInfrastructureFailure = (reasonCode: string) =>
  FAILURE_KIND_BY_REASON[reasonCode] === "infrastructure";

/** Reason codes that can appear on a failure, so PASS is excluded. */
export const FAILURE_REASON_CODES = (
  Object.keys(REASON_CODE_LABELS) as ReasonCode[]
).filter((reasonCode) => reasonCode !== "PASS");

/** Shown in place of the actions when a failure is not a question problem. */
export const INFRASTRUCTURE_FAILURE_HINT =
  "Most likely a service side or auth issue, not the question. Please fix that first, then re-run.";

/**
 * Sorts table rows, choosing the comparison per value pair rather than from the
 * first row.
 *
 * The shared sortBasedOnStrategy picks its strategy from `tableData[0]`, so a
 * null in the first row sends a whole Date column down the string path, where
 * `Date.toString()` begins with the weekday and orders Fri before Mon before
 * Wed. Several columns here are `Date | null`, so they need this instead.
 * Missing values sort last in both directions.
 */
export const sortRows = <T>(
  rows: T[],
  column: keyof T,
  direction: string,
): T[] => {
  const factor = direction === "ascending" ? 1 : -1;

  return [...rows].sort((rowA, rowB) => {
    const a = rowA[column];
    const b = rowB[column];

    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;

    if (a instanceof Date && b instanceof Date) {
      return (a.getTime() - b.getTime()) * factor;
    }
    if (typeof a === "number" && typeof b === "number") {
      return (a - b) * factor;
    }

    return (
      String(a).toLowerCase().localeCompare(String(b).toLowerCase()) * factor
    );
  });
};

export const formatCount = (value: number) => value.toLocaleString("en-US");

/** e.g. "10/07/2026" */
export const formatReportDate = (date: Date | null) =>
  date
    ? date.toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
      })
    : "";

/** e.g. "11:45 PDT" */
export const formatReportTime = (date: Date | null) =>
  date
    ? date.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
        timeZoneName: "short",
      })
    : "";

/** e.g. "10/07/2026 11:45 PDT" */
export const formatReportTimestamp = (date: Date | null) =>
  date ? `${formatReportDate(date)} ${formatReportTime(date)}` : "";

/** e.g. "Oct 4, 2026" */
/** Returns "" rather than undefined, so interpolating a missing date is blank. */
export const formatDate = (date: Date | null) =>
  date
    ? date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "";

export const formatFileSize = (bytes: number) =>
  bytes >= 1048576
    ? `${(bytes / 1048576).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;
